<script lang="ts" setup>
import { computed, ref } from "vue";
import { useMounted } from "@vueuse/core";
import ProductFlashOffer from "@/components/home/ProductFlashOffer.vue";
import ProductFlashOfferFirst from "@/components/home/ProductFlashOfferFirst.vue";
import { HomeService } from "~/services/HomeService";
import { Swiper, SwiperSlide } from "swiper/vue";
import {
  Navigation,
  Pagination,
  Scrollbar,
  A11y,
  EffectFade,
  FreeMode,
} from "swiper/modules";
import { Jsona } from "jsona";
import type { IProduct } from "~/models/Product";

const dataFormatter = new Jsona();
const modules = [Pagination, Scrollbar, A11y, Navigation, EffectFade, FreeMode];

const routeHydrated = useRouteHydrated();
const products = useState<IProduct[]>("home.flashProducts", () => []);
const seeMore = useState("home.seeMore", () => "/");
const page = useState("home.flashPage", () => 1);

const loading = ref(false);
const homeService = new HomeService();
const clicks = ref(0);
const mounted = useMounted();
const flashProducts = computed(() =>
  Array.isArray(products.value) ? products.value : [],
);
const hasProducts = computed(() => flashProducts.value.length > 0);
const flashProductsMobileFallback = computed(() =>
  flashProducts.value.slice(0, 4),
);

const getMoreProducts = () => {
  if (clicks.value === 0 && homeService) {
    clicks.value++;
    homeService.getFlashProducts(page.value).then((response) => {
      if (response) {
        const newProducts = dataFormatter.deserialize(response) as IProduct[];
        products.value = flashProducts.value.concat(
          newProducts.filter((p) => p.slug).slice(0, 4),
        );
        page.value++;
      }
    });
  } else {
    const path = "/buscar/oferta_relampago";
    window.location.href = path;
  }
};

const search = async () => {
  if (!routeHydrated.value) {
    loading.value = true;
    try {
      const response = await homeService.getFlashProducts(null);
      if (response) {
        const items = dataFormatter.deserialize(response) as IProduct[];
        products.value = items.filter((p) => p.slug);
        seeMore.value = response.links?.self || "/";
        page.value++;
      }
    } catch (err) {
      console.error(err);
    } finally {
      loading.value = false;
    }
  }
};

// const section = ref<HTMLElement>();
// let searched = false;
// useIntersectionObserver(section, ([observer]) => {
//   if (!searched && observer.isIntersecting) {
//     searched = true;
//     search();
//   }
// });
if (import.meta.server) {
  await search();
} else {
  search(); // en cliente mostramos pantallas de carga
}
</script>

<template>
  <section>
    <div v-if="hasProducts" class="mt-4 mt-lg-5 px-3 px-lg-0 flash-deals">
      <h2 class="text-secondary title">Ofertas relámpago</h2>
      <div
        v-if="!loading"
        class="row row-cols-2 row-cols-lg-3 row-cols-xl-4 row-gap-3 mt-3 d-none d-lg-flex"
      >
        <div
          v-for="(product, index) in flashProducts"
          :key="index"
          class="col px-1 px-lg-2"
        >
          <product-flash-offer-first v-if="index == 0" :product="product" />
          <product-flash-offer v-if="index > 0" :product="product" />
        </div>
      </div>
      <div v-if="!loading" class="mt-3 d-block d-lg-none">
        <div
          v-if="!mounted"
          class="flash-offer-mobile-fallback d-flex align-items-stretch"
        >
          <div
            v-for="(product, index) in flashProductsMobileFallback"
            :key="index"
            class="flash-offer-mobile-fallback__item"
          >
            <product-flash-offer-first v-if="index == 0" :product="product" />
            <product-flash-offer v-if="index > 0" :product="product" />
          </div>
        </div>
        <swiper
          v-else
          :modules="modules"
          :navigation="false"
          :loop="false"
          :pagination="false"
          :slides-per-view="2"
          :space-between="5"
          :scrollbar="{ draggable: true }"
          :free-mode="true"
          class="p-0"
        >
          <swiper-slide v-for="(product, index) in flashProducts" :key="index">
            <product-flash-offer-first v-if="index == 0" :product="product" />
            <product-flash-offer v-if="index > 0" :product="product" />
          </swiper-slide>
        </swiper>
      </div>
      <div
        v-if="loading"
        class="d-flex justify-content-center align-content-center p-2"
      >
        <div class="spinner-border text-secondary" role="status">
          <span class="visually-hidden mx-auto">Loading...</span>
        </div>
      </div>
      <div class="row px-2 mt-3 d-none d-lg-block">
        <button
          class="w-100 bg-body-tertiary border-0 fs-5 text-primary p-3"
          @click="getMoreProducts"
        >
          Ver más resultados
        </button>
      </div>
    </div>
  </section>
</template>

<style lang="sass" scoped>
.flash-offer-mobile-fallback
    overflow-x: auto
    scroll-snap-type: x proximity
    gap: 5px
    -webkit-overflow-scrolling: touch

.flash-offer-mobile-fallback__item
    flex: 0 0 calc(50% - 3px)
    min-width: 0
    scroll-snap-align: start
</style>
